<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="650"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar :class="[parseInt(orderType) === OrderTypeEnum.DELIVERY ? 'purple' : 'blue darken-3']">
            <v-icon dark>
              timer
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ parseInt(orderType) === OrderTypeEnum.DELIVERY ? $t('Pos.SelectOrderTypeTimeDialog.Toolbar.Title.Delivery') : $t('Pos.SelectOrderTypeTimeDialog.Toolbar.Title.Collection') }}
            </div>
            <div class="text-caption">
              {{ parseInt(orderType) === OrderTypeEnum.DELIVERY ? $t('Pos.SelectOrderTypeTimeDialog.Toolbar.Subtitle.Delivery') : $t('Pos.SelectOrderTypeTimeDialog.Toolbar.Subtitle.Collection') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-container
            v-if="$route.name === 'Pos' && scheduledOrderActive"
            fluid
          >
            <v-row dense>
              <v-col cols="12">
                <v-radio-group
                  v-model="scheduledOrder"
                  :disabled="scheduledLoading"
                  class="mt-0"
                  hide-details
                  mandatory
                  @change="setModelValue('scheduledOrder')"
                >
                  <v-radio
                    :disabled="scheduledLoading"
                    :style="scheduledOrder === OrderTimeEnum.SCHEDULED ? 'border: #37abf6 1px solid;' : 'border: #a6a6a6 1px solid;'"
                    class="rounded pa-3"
                    value="scheduled"
                  >
                    <template #label>
                      <div>
                        <div class="font-weight-bold">
                          {{ $t('Order.ScheduledOrder.Custom.Title') }}
                        </div>
                        <div class="caption grey--text">
                          {{ parseInt(orderType) === OrderTypeEnum.DELIVERY ? $t('Order.ScheduledOrder.Custom.Delivery') : $t('Order.ScheduledOrder.Custom.Collection') }}
                        </div>
                      </div>
                    </template>
                  </v-radio>

                  <v-slide-y-transition hide-on-leave>
                    <div
                      v-show="scheduledOrder === OrderTimeEnum.SCHEDULED"
                      class="mt-1 mb-6"
                    >
                      <v-container
                        fluid
                        pa-0
                      >
                        <v-row dense>
                          <v-col cols="6">
                            <v-select
                              v-model="scheduledOrderDate"
                              :dense="$vuetify.breakpoint.xsOnly"
                              :disabled="scheduledLoading"
                              :items="scheduledOrderDates"
                              :label="$tc('Common.Date.Day', 0)"
                              hide-details
                              item-text="Name"
                              item-value="Value"
                              outlined
                              return-object
                              @change="setModelValue('scheduledOrderDate')"
                            />
                          </v-col>

                          <v-col cols="6">
                            <v-select
                              v-model="scheduledOrderTime"
                              :dense="$vuetify.breakpoint.xsOnly"
                              :disabled="scheduledLoading"
                              :items="scheduledOrderTimes"
                              :label="$tc('Common.Date.Hour', 0)"
                              hide-details
                              item-text="Name"
                              item-value="Value"
                              outlined
                              return-object
                              @change="setModelValue('scheduledOrderTime')"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </div>
                  </v-slide-y-transition>

                  <v-radio
                    :disabled="scheduledLoading"
                    :style="scheduledOrder === OrderTimeEnum.NOW ? 'border: #37abf6 1px solid;' : 'border: #a6a6a6 1px solid;'"
                    class="rounded pa-3"
                    value="now"
                  >
                    <template #label>
                      <div>
                        <div class="font-weight-bold">
                          {{ $t('Order.ScheduledOrder.Default.Title') }}
                        </div>
                        <div class="caption grey--text">
                          {{ currentTime }} {{ $tc('Common.Date.Minute', 0) }}
                        </div>
                      </div>
                    </template>
                  </v-radio>

                  <v-slide-y-transition hide-on-leave>
                    <div
                      v-show="scheduledOrder === OrderTimeEnum.NOW"
                      class="mt-1 mb-6"
                    >
                      <v-container
                        fluid
                        pa-0
                      >
                        <v-row dense>
                          <v-col
                            v-for="(item, index) in times"
                            :key="index"
                            cols="4"
                          >
                            <v-list
                              class="pa-0"
                              two-line
                            >
                              <v-list-item
                                :class="[getItemColor(item).bgColor]"
                                class="pa-0"
                                ripple
                                @click="setTime(item)"
                              >
                                <v-list-item-content>
                                  <div
                                    class="text-center text-no-wrap font-weight-bold text-lowercase"
                                    style="width: 100%;"
                                  >
                                    <v-icon
                                      :color="getItemColor(item).iconColor"
                                      size="24"
                                    >
                                      timer
                                    </v-icon>
                                  </div>
                                  <div
                                    :class="[getItemColor(item).textColor, {'caption': $vuetify.breakpoint.xsOnly, 'title': $vuetify.breakpoint.smAndUp}]"
                                    class="text-center text-no-wrap font-weight-bold text-lowercase"
                                    style="width: 100%;"
                                  >
                                    {{ convertMinutesToDisplayString(item) }}
                                  </div>
                                </v-list-item-content>
                              </v-list-item>
                              <v-divider />
                            </v-list>
                          </v-col>

                          <v-col cols="12">
                            <v-checkbox
                              v-model="setAsDefault"
                              :label="parseInt(orderType) === OrderTypeEnum.DELIVERY ? $t('Pos.SelectOrderTypeTimeDialog.SetAsDefault.Delivery') : $t('Pos.SelectOrderTypeTimeDialog.SetAsDefault.Collection')"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </div>
                  </v-slide-y-transition>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-container>

          <v-container
            v-else
            fluid
          >
            <v-row dense>
              <v-col
                v-for="(item, index) in times"
                :key="index"
                cols="4"
              >
                <v-list
                  class="pa-0"
                  two-line
                >
                  <v-list-item
                    :class="[getItemColor(item).bgColor]"
                    class="pa-0"
                    ripple
                    @click="setTime(item)"
                  >
                    <v-list-item-content>
                      <div
                        class="text-center text-no-wrap font-weight-bold text-lowercase"
                        style="width: 100%;"
                      >
                        <v-icon
                          :color="getItemColor(item).iconColor"
                          size="24"
                        >
                          timer
                        </v-icon>
                      </div>
                      <div
                        :class="[getItemColor(item).textColor, {'caption': $vuetify.breakpoint.xsOnly, 'title': $vuetify.breakpoint.smAndUp}]"
                        class="text-center text-no-wrap font-weight-bold text-lowercase"
                        style="width: 100%;"
                      >
                        {{ convertMinutesToDisplayString(item) }}
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider />
                </v-list>
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="setAsDefault"
                  :label="parseInt(orderType) === OrderTypeEnum.DELIVERY ? $t('Pos.SelectOrderTypeTimeDialog.SetAsDefault.Delivery') : $t('Pos.SelectOrderTypeTimeDialog.SetAsDefault.Collection')"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import AppData from '@/mixins/appdata'
import OrderTypeTime from '@/mixins/orders/orderTypeTime'
import OrderTimeEnum from '@/api/Enums/OrderTimeEnum'
import OrderTypeEnum from '@/api/Enums/OrderTypeEnum'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'

export default {
  components: {},
  mixins    : [AppData, OrderTypeTime],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    selectedTime: {
      type    : [String, Number],
      required: true
    },
    orderType: {
      type    : [String, Number],
      required: true
    },
    updateStatus: {
      type   : Boolean,
      default: false
    },
    scheduledLoading: {
      type   : Boolean,
      default: false
    }
  },
  DataStore: {posCart: 'cart'},
  data() {
    return {
      scheduledOrder    : OrderTimeEnum.NOW,
      scheduledOrderDate: null,
      scheduledOrderTime: null,
      setAsDefault      : false,
      isTimeSelected    : false
    }
  },
  computed: {
    OrderTypeEnum() {
      return OrderTypeEnum
    },
    OrderTimeEnum() {
      return OrderTimeEnum
    },

    times() {
      return parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? this.deliveryTimeRange : this.collectionTimeRange
    },

    scheduledOrderConfigByOrderType() {
      const orderType = this?.posCart?.SOrderType
      return orderType === OrderTypeEnum.DELIVERY ? this.scheduledOrderConfig?.Delivery || {} : this.scheduledOrderConfig?.Collection || {}
    },

    scheduledOrderActive() {
      return this.scheduledOrderConfigByOrderType?.Active
          && (this.scheduledOrderConfigByOrderType?.Sources?.includes(SourceTypeEnum.ALL)
              || this.scheduledOrderConfigByOrderType?.Sources?.includes(this.posCart.SourceTypeId)
              || (this.scheduledOrderConfigByOrderType?.Sources?.includes(SourceTypeEnum.POS)
                  && [...SourceTypeEnum.POS_SOURCES, ...SourceTypeEnum.EXTERNAL_PLATFORMS, ...SourceTypeEnum.ONLINE_SOURCES].includes(this.posCart.SourceTypeId)
              )
          )
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },

    currentTime: {
      get() {
        return this.selectedTime
      },
      set(val) {
        this.$emit('update:selectedTime', val)
      }
    },

    currentStatus: {
      get() {
        return this.updateStatus
      },
      set(val) {
        this.$emit('update:updateStatus', val)
      }
    },

    scheduledOrderDates() {
      const dates = []
      const config = {
        sameDay : `[${this.$t('Common.Date.Today')}]`,
        nextDay : `[${this.$t('Common.Date.Tomorrow')}]`,
        nextWeek: 'ddd, DD/MM',
        lastDay : 'DD/MM/YYYY',
        lastWeek: 'DD/MM/YYYY',
        sameElse: 'DD/MM/YYYY'
      }

      const daysAhead = this.scheduledOrderConfigByOrderType?.DaysAhead ?? 5
      const minOrderMinutesAhead = this.scheduledOrderConfigByOrderType?.MinimumOrderMinutesAhead ?? 30
      const timeNow = this.$dayjs().add(minOrderMinutesAhead, 'minute')

      for (let d = timeNow.isToday() ? 0 : 1; d < daysAhead; d++) {
        const date = this.$dayjs().add(d, 'day')
        const obj = {
          Name : date.calendar(this.$dayjs(), config),
          Value: this.$dayjs(date).format('YYYY-MM-DD')
        }
        dates.push(obj)
      }

      return dates
    },

    scheduledOrderTimes() {
      const times = []
      const startTime = this.scheduledOrderConfigByOrderType?.StartTime ?? 10
      const endTime = this.scheduledOrderConfigByOrderType?.EndTime ?? 23
      const minOrderMinutesAhead = this.scheduledOrderConfigByOrderType?.MinimumOrderMinutesAhead ?? 30
      const isTodaySelected = this.scheduledOrderDate?.Value === this.scheduledOrderDates[0]?.Value
      const timeNow = this.$dayjs().add(minOrderMinutesAhead, 'minute')
      const hourNow = isTodaySelected && timeNow.isToday() ? parseInt(timeNow.hour()) : startTime
      const minNow = isTodaySelected && timeNow.isToday() ? parseInt(timeNow.minute()) : minOrderMinutesAhead

      for (let h = hourNow; h < endTime; h++) {
        for (let m = 0; m < 60; m = m + 15) {
          if ((h === hourNow && m >= minNow && h >= startTime && h <= endTime) || (h > hourNow && h >= startTime && h <= endTime)) {
            //if ((h === hourNow && m >= minNow) || h > hourNow) {
            const hour = String(h).padStart(2, '0')
            const min = String(m).padStart(2, '0')
            const time = `${hour}:${min}`
            times.push({
              Name : time,
              Value: time
            })
          }
        }
      }

      return times
    }
  },
  watch: {
    scheduledOrder(newVal) {
      if (newVal === OrderTimeEnum.NOW) {
        this.scheduledOrderDate = null
        this.scheduledOrderTime = null
      } else {
        this.scheduledOrderDate = this.scheduledOrderDates[0] || null
        this.scheduledOrderTime = this.scheduledOrderTimes[0] || null
      }
    },

    scheduledOrderDate(newVal, oldVal) {
      this.$nextTick(() => {
        if (newVal) {
          if (newVal?.Value !== oldVal?.Value) {
            const scheduledOrderTimeIndex = this.scheduledOrderTimes.findIndex(item => item.Value === this.scheduledOrderTime?.Value)
            this.scheduledOrderTime = scheduledOrderTimeIndex > -1 ? this.scheduledOrderTimes[scheduledOrderTimeIndex] : this.scheduledOrderTimes[0] || null
          }
        } else {
          this.scheduledOrderTime = null
        }
      })
    },

    'posCart.ScheduledOrder': {
      handler(newVal, oldVal) {
        this.updateValueFromModel()
      },
      immediate: true,
      deep     : true
    },

    visible(newVal) {
      if (newVal) {
        this.updateValueFromModel()
        this.isTimeSelected = false
        this.setAsDefault = false
      }
    }
  },
  methods: {
    updateValueFromModel() {
      this.scheduledOrder = this.posCart?.ScheduledOrder?.Type || OrderTimeEnum.NOW

      if (this.scheduledOrder === OrderTimeEnum.NOW) {
        this.scheduledOrderDate = null
        this.scheduledOrderTime = null

      } else {
        this.$nextTick(() => {
          const scheduledOrderDateIndex = this.scheduledOrderDates.findIndex(item => item.Value === this.posCart?.ScheduledOrder?.Date)
          this.scheduledOrderDate = scheduledOrderDateIndex > -1 ? this.scheduledOrderDates[scheduledOrderDateIndex] : null

          const scheduledOrderTimeIndex = this.scheduledOrderTimes.findIndex(item => item.Value === this.posCart?.ScheduledOrder?.Time)
          this.scheduledOrderTime = scheduledOrderTimeIndex > -1 ? this.scheduledOrderTimes[scheduledOrderTimeIndex] : null

          this.$nextTick(() => {
            if (!this.scheduledOrderDate || !this.scheduledOrderTime) {
              this.scheduledOrder = OrderTimeEnum.NOW
              this.setModelValue('updateValueFromModel')
            }
          })
        })
      }
    },

    setModelValue(t) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          const retVal = {
            Type    : this.scheduledOrder,
            Date    : this.scheduledOrder === OrderTimeEnum.NOW ? null : this.scheduledOrderDate?.Value || null,
            Time    : this.scheduledOrder === OrderTimeEnum.NOW ? null : this.scheduledOrderTime?.Value || null,
            DateTime: this.scheduledOrder === OrderTimeEnum.NOW ? null : this.scheduledOrderDate?.Value && this.scheduledOrderTime?.Value ? `${this.scheduledOrderDate.Value} ${this.scheduledOrderTime.Value}:00` : null
          }


          this.$nextTick(() => {
            if ((retVal.Type === OrderTimeEnum.NOW && !retVal.Date && !retVal.Time && !retVal.DateTime)
                || (retVal.Type === OrderTimeEnum.SCHEDULED && retVal.Date && retVal.Time && retVal.DateTime)
            ) {
              this.$emit('cart:update-scheduled', retVal)
            }
          })
        })
      })
    },

    setTime(time) {
      if (this.isTimeSelected) return
      this.isTimeSelected = true
      this.currentTime = parseInt(time)
      this.currentStatus = true
      this.isVisible = false

      this.$emit('update-order-time', {
        time        : parseInt(time),
        setAsDefault: this.setAsDefault ?? false
      })

      this.$emit('cart:order-type-time-selected', parseInt(time))
    },
    onCancelButtonClick() {
      this.setAsDefault = false
      this.currentStatus = false
      this.isVisible = false
    },
    getItemColor(item) {
      const retVal = {
        icon     : 'timer',
        iconColor: parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? 'purple' : 'blue darken-3',
        bgColor  : this.isDarkMode ? 'grey darken-3' : 'grey lighten-4',
        textColor: this.isDarkMode ? 'grey--text text--lighten-1' : 'grey--text text--darken-1'
      }
      const orderDefaultTime = parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? this.orderTypeDefaultTimes.delivery_time : this.orderTypeDefaultTimes.collection_time

      if (this.currentTime.toString() === item.toString()) {
        retVal.iconColor = parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? 'purple' : 'blue darken-3'
        retVal.bgColor = parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? (this.isDarkMode ? 'purple darken-4' : 'purple lighten-5') : (this.isDarkMode ? 'blue darken-4' : 'blue lighten-5')
        retVal.textColor = parseInt(this.orderType) === OrderTypeEnum.DELIVERY ? 'purple--text' : 'blue--text text--darken-3'
      }

      if (orderDefaultTime.toString() === item.toString()) {
        retVal.iconColor = 'green'
      }

      return retVal
    }
  }
}
</script>

<style scoped>
:deep(.v-list__tile--link) {
  padding: 0 !important;
}
</style>
